/**
 * ConfidenceLogDashboardCard — the Dashboard home for the daily Confidence
 * Log. Once a client has written a real line for today (>1 word) it leaves
 * the Daily Check-in and surfaces here, taking the slot the KPI-trend card
 * used to occupy (the per-day KPI data already lives in the monthly tracker).
 *
 * Shows the most recent entry prominently + the last few days as a compact
 * history. Self-gates on the client's step (Confidence Log unlocks at Step 3)
 * via `useClientStep`; coaches always see the live card.
 *
 * CL-EDIT (2026-06-27) — today's line is editable in place. After the
 * Check-in card hands the day's entry off here, this is where the client
 * tweaks it: an inline Edit reveals a textarea + Save/Cancel, persists via
 * the same UPSERT, and updates the card without a reload.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientStep } from '@/lib/use-client-step';

interface ConfidenceLogDashboardCardProps {
  readonly clientId: string;
}

function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function ConfidenceLogDashboardCard({
  clientId,
}: ConfidenceLogDashboardCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const { step, isClient } = useClientStep();
  const isLocked = isClient && (step ?? 0) < 3;

  const [entries, setEntries] = useState<api.ConfidenceLogEntry[]>([]);
  const [hydrated, setHydrated] = useState(false);

  // CL-EDIT — inline edit of today's entry.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId || isLocked) {
      setHydrated(true);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const list = await api.getConfidenceLogEntries(accessToken, clientId, {
          from: isoDaysAgo(13),
          to: todayIso(),
        });
        if (active) setEntries(list.filter((e) => e.entryText.trim().length > 0));
      } catch {
        /* swallow — render the empty state */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, isLocked]);

  const sorted = [...entries].sort((a, b) => (a.date < b.date ? 1 : -1));
  const latest = sorted[0];
  const history = sorted.slice(1, 4);
  // Only today's line is editable here (the day's Confidence Log); older
  // entries stay read-only history.
  const canEditToday = !isLocked && latest?.date === todayIso();

  function startEdit(): void {
    setDraft(latest?.entryText ?? '');
    setEditError(null);
    setEditing(true);
  }

  function cancelEdit(): void {
    setEditing(false);
    setEditError(null);
  }

  async function saveEdit(): Promise<void> {
    if (!latest) return;
    setEditError(null);
    setSavingEdit(true);
    try {
      const result = await api.upsertConfidenceLogEntry(
        accessToken,
        clientId,
        latest.date,
        draft,
      );
      const savedText = result?.entryText ?? draft;
      setEntries((prev) =>
        prev.map((e) => (e.date === latest.date ? { ...e, entryText: savedText } : e)),
      );
      setEditing(false);
    } catch {
      setEditError(t('dashboard.cards.confidenceLog.saveError'));
    } finally {
      setSavingEdit(false);
    }
  }

  const dateLocale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const prettyDate = (iso: string): string =>
    new Date(`${iso}T00:00:00`).toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
    });

  return (
    <article
      className="bg-bone-2 rounded-lg p-s5 border border-bone-3 shadow-sm"
      data-testid="card-confidence-log"
    >
      <h2 className="font-display text-display-sm text-ink mb-s3">
        {t('dashboard.cards.confidenceLog.title')}
      </h2>

      {isLocked ? (
        <div className="flex items-start gap-s3" data-testid="confidence-log-dashboard-locked">
          <span aria-hidden="true" className="text-flame-deep text-xl mt-s1">🔒</span>
          <p className="text-sm text-gray-2">
            {t('dashboard.cards.confidenceLog.locked')}
          </p>
        </div>
      ) : latest ? (
        <div className="space-y-s3" data-testid="confidence-log-dashboard-latest">
          {editing ? (
            <div className="space-y-s2" data-testid="confidence-log-dashboard-edit">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="input-field min-h-24"
                rows={4}
                autoFocus
                data-testid="confidence-log-dashboard-edit-textarea"
              />
              {editError && (
                <p role="alert" className="text-xs text-flame-deep" data-testid="confidence-log-dashboard-edit-error">
                  {editError}
                </p>
              )}
              <div className="flex items-center gap-s3">
                <button
                  type="button"
                  onClick={() => void saveEdit()}
                  disabled={savingEdit}
                  className="btn-primary text-sm"
                  data-testid="confidence-log-dashboard-edit-save"
                >
                  {savingEdit
                    ? t('dashboard.cards.confidenceLog.saving')
                    : t('dashboard.cards.confidenceLog.save')}
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  disabled={savingEdit}
                  className="text-sm font-semibold text-gray-2 hover:text-ink transition-colors"
                  data-testid="confidence-log-dashboard-edit-cancel"
                >
                  {t('dashboard.cards.confidenceLog.cancel')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <blockquote className="text-base text-ink leading-relaxed border-l-4 border-flame pl-s3 whitespace-pre-wrap">
                {latest.entryText}
              </blockquote>
              <div className="flex items-center justify-between gap-s3">
                <p className="text-xs font-mono uppercase tracking-wide text-gray-1">
                  {prettyDate(latest.date)}
                </p>
                {canEditToday && (
                  <button
                    type="button"
                    onClick={startEdit}
                    className="text-xs font-semibold text-flame-deep hover:text-flame transition-colors"
                    data-testid="confidence-log-dashboard-edit-start"
                  >
                    {t('dashboard.cards.confidenceLog.edit')}
                  </button>
                )}
              </div>
            </>
          )}
          {!editing && history.length > 0 && (
            <ul className="mt-s2 space-y-s2 border-t border-bone-3 pt-s3">
              {history.map((e) => (
                <li key={e.id} className="text-sm text-gray-2" data-testid="confidence-log-dashboard-history">
                  <span className="font-mono text-xs text-gray-1 mr-s2">{prettyDate(e.date)}</span>
                  <span className="text-ink/80">{e.entryText}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : hydrated ? (
        <p className="text-sm text-gray-2" data-testid="confidence-log-dashboard-empty">
          {t('dashboard.cards.confidenceLog.empty')}
        </p>
      ) : (
        <p className="text-sm text-gray-2">{t('common.loading')}</p>
      )}
    </article>
  );
}
